import { useTranslation } from 'next-i18next';
import React from 'react';
import IntlTelInput, { CountryData } from 'react-intl-tel-input';
import { IError } from '../../../core/shared/checker';
import {
  getErrorById,
  isValidFieldById,
} from '../../../core/shared/checkerUtils';
import { FormControlError } from '../../../core/shared/forms/useForm';
import { getPhoneNumberWithoutCountryPrefix } from '../../../utils/phone';
import { NonEmptyArray } from '../../../utils/types';

export type ITelephoneFieldProps = {
  label: string;
  placeholder: string;
  value: string;
  isTouched?: boolean;
  onChange: (value: string, countryCode: string | undefined) => void;
} & (
  | {
      inputID: string;
      onBlur?: (ID: string, value: string) => void;
      errors: IError[];
    }
  | {
      onBlur?: (value: string) => void;
      errors?: NonEmptyArray<FormControlError>;
    }
);

export const TelephoneInputField: React.FC<ITelephoneFieldProps> = ({
  label,
  placeholder,
  value,
  isTouched = true,
  onChange,
  ...props
}) => {
  const { t } = useTranslation();
  let isInErrorStatus: boolean;
  let errorMessage: string | undefined;
  if ('inputID' in props) {
    const isValid = isValidFieldById(props.errors, props.inputID);
    const error: IError = getErrorById(props.errors, props.inputID);
    errorMessage = isValid ? '' : t(error.resourceKey);
    isInErrorStatus = !isValid && !!isTouched;
  } else {
    errorMessage = props.errors?.map(({ message }) => message).join(', ');
    isInErrorStatus = !!errorMessage && !!isTouched;
  }

  const getValue = (
    selectedCountryData: CountryData,
    telephoneNumber: string
  ) => {
    const phone = getPhoneNumberWithoutCountryPrefix(telephoneNumber);
    if (phone?.length)
      return `+${selectedCountryData.dialCode}${phone
        .trim()
        .split(' ')
        .join('')}`;
    return '';
  };

  return (
    <div className="login__form__field__row">
      <label className="login__form__field__row__label">{label}</label>
      <IntlTelInput
        defaultCountry="it"
        preferredCountries={['it', 'de', 'fr', 'es']}
        telInputProps={
          {
            'data-error': props.errors?.length ? 'error' : '',
          } as React.InputHTMLAttributes<HTMLInputElement> & {
            'data-error': string;
          }
        }
        inputClassName={`input_style1 ${
          isInErrorStatus ? 'input_style1--error' : ''
        }`}
        placeholder={placeholder}
        defaultValue={value}
        onSelectFlag={(currentNumber, selectedCountryData) => {
          const valueToPass = getValue(selectedCountryData, currentNumber);
          onChange(valueToPass, selectedCountryData.iso2);
        }}
        onPhoneNumberChange={(_, newNumber, selectedCountryData) => {
          const valueToPass = getValue(selectedCountryData, newNumber);
          onChange(valueToPass, selectedCountryData.iso2);
        }}
        onPhoneNumberBlur={(_, newNumber, selectedCountryData) => {
          const valueToPass = getValue(selectedCountryData, newNumber);
          if ('inputID' in props) {
            props.onBlur && props.onBlur(props.inputID, valueToPass);
          } else {
            props.onBlur && props.onBlur(valueToPass);
          }
        }}
      />
      {isInErrorStatus && (
        <p className="assistive_text assistive_text--error">{errorMessage}</p>
      )}
    </div>
  );
};
